import React from 'react';
import { X, Calendar, CreditCard, CheckCircle, Clock } from 'lucide-react';
import ModalShell from '../shared/ModalShell';
import {
  formatDisplayMoney,
  paymentCatalogAmount,
  paymentVendorShareDisplay,
} from '../../utils/formatMoney';

const PaymentHistoryModal = ({
  isOpen,
  onClose,
  payments = [],
  isVendorView = false,
  displayCurrency = 'USD',
  displayLocale = 'en-US',
}) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatAmount = (amount) =>
    formatDisplayMoney(amount, displayCurrency, displayLocale);

  const getPaymentTypeLabel = (type) =>
    type === 'deposit' ? 'Deposit' : 'Full payment';

  const completedPayments = payments.filter((p) => p.status === 'completed');

  const totalPaid = completedPayments.reduce((sum, p) => {
    if (isVendorView) {
      return sum + paymentVendorShareDisplay(p);
    }
    return sum + paymentCatalogAmount(p);
  }, 0);

  const getStatusLabel = (status) => {
    const statusMap = {
      completed: 'Completed',
      pending: 'Pending',
      failed: 'Failed',
      cancelled: 'Cancelled',
      refunded: 'Refunded',
    };
    return (
      statusMap[status] ||
      status?.charAt(0).toUpperCase() + status?.slice(1) ||
      'Unknown'
    );
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      labelledBy="payment-history-title"
      panelClassName="max-w-2xl"
    >
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xl overflow-hidden">
        <div className="flex items-start justify-between gap-3 px-5 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-800">
          <div>
            <h2
              id="payment-history-title"
              className="text-lg font-semibold text-gray-900 dark:text-gray-50 tracking-tight"
            >
              Payment history
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Transactions for this booking
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 !rounded-xl text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {completedPayments.length > 0 && (
          <div className="px-5 sm:px-6 py-4 bg-gray-50 dark:bg-gray-950/50 border-b border-gray-200 dark:border-gray-800">
            <div className="flex justify-between items-center gap-3">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {isVendorView ? 'Total service amount' : 'Total paid'}
              </span>
              <span className="text-lg font-bold text-gray-900 dark:text-gray-50 tabular-nums">
                {formatAmount(totalPaid)}
              </span>
            </div>
            <div className="flex justify-between items-center gap-3 mt-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Transactions
              </span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100 tabular-nums">
                {completedPayments.length}
              </span>
            </div>
          </div>
        )}

        <div className="px-5 sm:px-6 py-5 max-h-[55vh] overflow-y-auto">
          {completedPayments.length === 0 ? (
            <div className="text-center py-10">
              <CreditCard className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-600 dark:text-gray-400">
                No completed payments yet
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {completedPayments.map((payment) => {
                const amount = isVendorView
                  ? paymentVendorShareDisplay(payment)
                  : paymentCatalogAmount(payment);
                const done = payment.status === 'completed';

                return (
                  <div
                    key={payment.id}
                    className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-950/40 p-4"
                  >
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          {done ? (
                            <CheckCircle className="w-4 h-4 text-emerald-600" />
                          ) : (
                            <Clock className="w-4 h-4 text-gray-400" />
                          )}
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              done
                                ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200'
                                : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                            }`}
                          >
                            {getStatusLabel(payment.status)}
                          </span>
                        </div>
                        {payment.payment_reference ? (
                          <div className="flex items-center gap-2 mb-1">
                            <CreditCard className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 break-all">
                              {payment.payment_reference}
                            </span>
                          </div>
                        ) : null}
                        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                          <Calendar className="w-3.5 h-3.5 shrink-0" />
                          <span>{formatDate(payment.created_at)}</span>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-lg font-bold text-gray-900 dark:text-gray-50 tabular-nums">
                          {formatAmount(amount)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {getPaymentTypeLabel(payment.payment_type)}
                        </div>
                      </div>
                    </div>

                    <div className="pt-3 border-t border-gray-200 dark:border-gray-700 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-gray-500">Method</span>
                        <span className="ml-2 font-medium text-gray-900 dark:text-gray-100 capitalize">
                          {payment.payment_method || 'Card'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Processor</span>
                        <span className="ml-2 font-medium text-gray-900 dark:text-gray-100 capitalize">
                          {payment.payment_processor || 'Stripe'}
                        </span>
                      </div>
                      {payment.transaction_id ? (
                        <div className="sm:col-span-2">
                          <span className="text-gray-500">Transaction ID</span>
                          <span className="ml-2 font-medium text-gray-900 dark:text-gray-100 break-all">
                            {payment.transaction_id}
                          </span>
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-5 sm:px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950/40">
          <button
            type="button"
            onClick={onClose}
            className="w-full px-4 py-2.5 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 !rounded-xl hover:bg-black dark:hover:bg-white transition-colors text-sm font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </ModalShell>
  );
};

export default PaymentHistoryModal;
